/**
 * Utility for managing app icon badge counts using the Badging API
 * Works with PWAs and "Install Page as App" on supported browsers
 * (Chrome, Edge, Opera on desktop and Android)
 */

/**
 * Check if the Badging API is supported
 */
export const isBadgingSupported = (): boolean => {
  return 'setAppBadge' in navigator && 'clearAppBadge' in navigator;
};

/**
 * Set a numeric badge on the app icon
 * @param count - Number to display on the badge (0-99 recommended)
 */
export const setAppBadge = async (count: number): Promise<void> => {
  if (!isBadgingSupported()) {
    console.warn('Badging API not supported in this browser');
    return;
  }

  try {
    if (count > 0) {
      await navigator.setAppBadge(count);
    } else {
      await navigator.clearAppBadge();
    }
  } catch (error) {
    console.error('Failed to set app badge:', error);
  }
};

/**
 * Clear the badge from the app icon
 */
export const clearAppBadge = async (): Promise<void> => {
  if (!isBadgingSupported()) {
    return;
  }

  try {
    await navigator.clearAppBadge();
  } catch (error) {
    console.error('Failed to clear app badge:', error);
  }
};

/**
 * Increment the current badge count
 * Note: This requires tracking the count in your app state
 */
export const incrementBadge = async (currentCount: number): Promise<void> => {
  await setAppBadge(currentCount + 1);
};

/**
 * Decrement the current badge count
 * Note: This requires tracking the count in your app state
 */
export const decrementBadge = async (currentCount: number): Promise<void> => {
  const newCount = Math.max(0, currentCount - 1);
  await setAppBadge(newCount);
};
